import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from './components/Navbar';
import Footer from '../routes/components/Footer';
import SocialItens from './components/SocialItens';
import LightBox from './components/LightBox';

import civil from './assets/images/1.jpg';
import iluminacao from './assets/images/2.png';
import transmissao from './assets/images/3.jpg';
import eolica from './assets/images/4.jpg';
import aeroporto from './assets/images/6.jpg';
import cabeamento from './assets/images/7.jpeg';
import construcao from './assets/images/8.jpg';
import { FiAward, FiMapPin, FiShield } from 'react-icons/fi';

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0 }
};

const stagger = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.12
    }
  }
};

const sectionReveal = {
  initial: 'hidden',
  whileInView: 'show',
  viewport: { once: true, amount: 0.18 },
  variants: fadeUp,
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] }
};

function SectionTag({ children, light = false }) {
  return (
    <span
      style={{
        display: 'inline-block',
        marginBottom: '14px',
        fontSize: '0.82rem',
        fontWeight: 700,
        letterSpacing: '1.4px',
        textTransform: 'uppercase',
        color: light ? '#d6a04a' : '#b7832f'
      }}
    >
      {children}
    </span>
  );
}

function SectionTitle({ children, center = false, light = false, maxWidth = '760px' }) {
  return (
    <h2
      style={{
        fontSize: 'clamp(2rem, 3vw, 3.2rem)',
        lineHeight: 1.12,
        fontWeight: 800,
        letterSpacing: '-0.8px',
        color: light ? '#ffffff' : '#0d2e63',
        marginBottom: '18px',
        maxWidth,
        marginLeft: center ? 'auto' : 0,
        marginRight: center ? 'auto' : 0
      }}
    >
      {children}
    </h2>
  );
}

function SectionText({ children, center = false, light = false, maxWidth = '760px' }) {
  return (
    <p
      style={{
        fontSize: '1.04rem',
        lineHeight: 1.9,
        color: light ? 'rgba(255,255,255,0.78)' : '#5f7088',
        maxWidth,
        margin: center ? '0 auto' : 0
      }}
    >
      {children}
    </p>
  );
}

function Home() {
  const [homeData, setHomeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const differentialIcons = {
    'Excelência operacional': FiAward,
    'Presença nacional': FiMapPin,
    'Segurança e conformidade': FiShield
  };
  useEffect(() => {
    async function loadHome() {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'https://real-backend-2026.onrender.com';
        const response = await fetch(`${apiUrl}/api/public/home`);

        if (!response.ok) {
          throw new Error('Falha ao buscar home pública');
        }

        const result = await response.json();
        setHomeData(result.data || null);
      } catch (error) {
        console.error('Erro ao carregar conteúdo da home:', error);
      } finally {
        setLoading(false);
      }
    }

    loadHome();
  }, []);

  const fallbackServices = useMemo(
    () => [
      {
        title: 'Saneamento',
        imageUrl: civil,
        description:
          'Atuação em operação, manutenção, construção e ampliação de sistemas de distribuição de água e esgotamento sanitário, com foco em eficiência e continuidade dos serviços.',
        link: '/about'
      },
      {
        title: 'Tecnologia da Informação',
        imageUrl: cabeamento,
        description:
          'Soluções em videomonitoramento, infraestrutura de rede, logística e ativos tecnológicos para apoiar operações mais seguras, conectadas e bem estruturadas.',
        link: '/about'
      },
      {
        title: 'Linhas de Transmissão',
        imageUrl: transmissao,
        description:
          'Operação, manutenção, construção e ampliação de linhas de transmissão de até 500 kV, subestações, faixas de servidão e estruturas metálicas.',
        link: '/about'
      },
      {
        title: 'Iluminação',
        imageUrl: iluminacao,
        description:
          'Gestão de parques de iluminação, projetos de iluminação cênica e decorativa, além de iniciativas voltadas à eficiência energética e melhoria urbana.',
        link: '/about'
      },
      {
        title: 'Energia Eólica',
        imageUrl: eolica,
        description:
          'Serviços de operação e manutenção de torres de medição eólica e aerogeradores, com suporte técnico orientado à confiabilidade dos ativos.',
        link: '/about'
      },
      {
        title: 'Aeroportos',
        imageUrl: aeroporto,
        description:
          'Atuação em operação e manutenção de sistemas aeroportuários, com compromisso técnico, organização operacional e atenção à continuidade dos processos.',
        link: '/about'
      },
      {
        title: 'Construção Civil',
        imageUrl: construcao,
        description:
          'Execução de obras, reformas e serviços de manutenção civil, com foco em qualidade construtiva, planejamento e desempenho operacional.',
        link: '/about'
      }
    ],
    []
  );

  const fallbackDifferentials = useMemo(
    () => [
      {
        title: 'Excelência operacional',
        text: 'Estrutura preparada para atuar em obras, operação e manutenção com organização, qualidade técnica e compromisso com a entrega.'
      },
      {
        title: 'Presença nacional',
        text: 'Atuação em diferentes regiões do Brasil, com capacidade de mobilização para atender demandas em Norte, Nordeste, Sudeste e Sul.'
      },
      {
        title: 'Segurança e conformidade',
        text: 'Processos, treinamentos e equipamentos voltados à proteção das equipes, à execução segura dos serviços e à confiabilidade de cada operação.'
      }
    ],
    []
  );

  const fallbackStats = useMemo(
    () => [
      { value: '50+', label: 'obras em andamento' },
      { value: '1992', label: 'ano de fundação' },
      { value: '4 regiões', label: 'de atuação no Brasil' },
      { value: '100%', label: 'compromisso com execução segura' }
    ],
    []
  );

  const fallbackAboutCards = useMemo(
    () => [
      {
        title: 'Atuação multissetorial',
        text: 'Presença em energia, saneamento, aeroportos, construção civil, iluminação e tecnologia.'
      },
      {
        title: 'Presença institucional',
        text: 'Uma empresa que combina experiência de campo com uma postura profissional e confiável.'
      }
    ],
    []
  );

  const primaryButton = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    textDecoration: 'none',
    background: 'linear-gradient(135deg, #d6a04a, #b9832d)',
    color: '#ffffff',
    padding: '15px 26px',
    borderRadius: '14px',
    fontWeight: 700,
    border: 'none',
    boxShadow: '0 18px 40px rgba(185, 131, 45, 0.22)',
    transition: 'all 0.3s ease'
  };

  const secondaryButton = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    textDecoration: 'none',
    background: 'rgba(255,255,255,0.08)',
    color: '#ffffff',
    padding: '15px 26px',
    borderRadius: '14px',
    fontWeight: 700,
    border: '1px solid rgba(255,255,255,0.18)',
    backdropFilter: 'blur(10px)',
    transition: 'all 0.3s ease'
  };

  const hero = homeData?.hero || {};
  const about = homeData?.about || {};
  const careers = homeData?.careers || {};
  const portfolio = homeData?.portfolio || {};
  const finalCta = homeData?.finalCta || {};

  const stats = homeData?.stats?.length ? homeData.stats : fallbackStats;
  const differentials = homeData?.differentials?.length ? homeData.differentials : fallbackDifferentials;
  const aboutCards = about?.cards?.length ? about.cards : fallbackAboutCards;

  const services = homeData?.services?.items?.length
    ? homeData.services.items.map((item, index) => ({
      ...item,
      imageUrl: item.imageUrl || fallbackServices[index]?.imageUrl || civil
    }))
    : fallbackServices;

  const heroBackgroundImage = hero.backgroundImageUrl || transmissao;
  const heroSideImage = hero.sideImageUrl || civil;
  const aboutImage1 = about.image1Url || construcao;
  const aboutImage2 = about.image2Url || iluminacao;
  const finalCtaBackground = finalCta.backgroundImageUrl || aeroporto;

  if (loading && !homeData) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #f3f6fb 0%, #ffffff 30%, #f7f9fc 100%)'
        }}
      >
        <Navbar />
      </div>
    );
  }

  return (
    <div
      style={{
        background: 'linear-gradient(180deg, #f3f6fb 0%, #ffffff 30%, #f7f9fc 100%)',
        color: '#11233e',
        fontFamily: 'Arial, sans-serif',
        overflow: 'hidden'
      }}
    >
      <Navbar />

      <section
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          padding: '120px 0 110px',
          backgroundImage: `linear-gradient(95deg, rgba(8,20,41,0.95) 0%, rgba(10,33,70,0.9) 42%, rgba(13,52,110,0.68) 100%), url(${heroBackgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 15% 20%, rgba(255,255,255,0.08), transparent 22%), radial-gradient(circle at 85% 12%, rgba(214,160,74,0.18), transparent 16%)'
          }}
        />

        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="row align-items-center g-5">
            <div className="col-lg-7">
              <motion.div {...sectionReveal} style={{ maxWidth: '720px', padding: '20px 0' }}>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 16px',
                    borderRadius: '999px',
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    color: 'rgba(255,255,255,0.84)',
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    marginBottom: '24px'
                  }}
                >
                  {hero.badge || 'RealEnergy • Energia, infraestrutura e serviços técnicos'}
                </div>

                <h1
                  style={{
                    fontSize: 'clamp(2.6rem, 5vw, 5rem)',
                    lineHeight: 1.03,
                    fontWeight: 800,
                    letterSpacing: '-1.2px',
                    color: '#ffffff',
                    marginBottom: '22px'
                  }}
                >
                  {hero.title || 'Soluções em engenharia, operação e infraestrutura com padrão profissional.'}
                </h1>

                <p
                  style={{
                    fontSize: '1.08rem',
                    lineHeight: 1.9,
                    color: 'rgba(255,255,255,0.8)',
                    maxWidth: '650px',
                    marginBottom: '34px'
                  }}
                >
                  {hero.subtitle ||
                    'Desde 1992, a RealEnergy atua com seriedade, capacidade técnica e compromisso com resultados, oferecendo suporte a projetos e operações em diferentes segmentos e regiões do Brasil.'}
                </p>

                <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '38px' }}>
                  <motion.div whileHover={{ y: -2, scale: 1.01 }} whileTap={{ scale: 0.98 }}>
                    <Link to={hero?.primaryButton?.link || '/contato'} style={primaryButton}>
                      {hero?.primaryButton?.text || 'Entrar em contato'}
                    </Link>
                  </motion.div>

                  <motion.div whileHover={{ y: -2, scale: 1.01 }} whileTap={{ scale: 0.98 }}>
                    <Link to={hero?.secondaryButton?.link || '/about'} style={secondaryButton}>
                      {hero?.secondaryButton?.text || 'Conheça a empresa'}
                    </Link>
                  </motion.div>
                </div>

                <motion.div
                  className="row g-3"
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                  style={{ marginTop: '8px' }}
                >
                  {stats.map((item, index) => (
                    <div className="col-6 col-md-3" key={item.id || index}>
                      <motion.div
                        whileHover={{ y: -6, backgroundColor: 'rgba(255,255,255,0.1)' }}
                        style={{
                          padding: '16px 14px',
                          borderRadius: '18px',
                          background: 'rgba(255,255,255,0.06)',
                          border: '1px solid rgba(255,255,255,0.12)',
                          backdropFilter: 'blur(10px)',
                          minHeight: '96px',
                          height: '100%'
                        }}
                      >
                        <div style={{ color: '#ffffff', fontWeight: 800, fontSize: '1.4rem', marginBottom: '4px' }}>
                          {item.value}
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.9rem', lineHeight: 1.5 }}>
                          {item.label}
                        </div>
                      </motion.div>
                    </div>
                  ))}
                </motion.div>
              </motion.div>
            </div>

            <div className="col-lg-5">
              <motion.div
                initial={{ opacity: 0, y: 34 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.08 }}
                style={{ position: 'relative', maxWidth: '500px', marginLeft: 'auto' }}
              >
                <motion.div
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                  style={{
                    position: 'relative',
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: '30px',
                    padding: '18px',
                    backdropFilter: 'blur(16px)',
                    boxShadow: '0 30px 80px rgba(0,0,0,0.24)'
                  }}
                >
                  <img
                    src={heroSideImage}
                    alt="Projetos RealEnergy"
                    style={{
                      width: '100%',
                      height: '430px',
                      objectFit: 'cover',
                      borderRadius: '24px',
                      display: 'block'
                    }}
                  />

                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.25 }}
                    whileHover={{ y: -4 }}
                    style={{
                      position: 'absolute',
                      left: '-20px',
                      bottom: '34px',
                      width: '235px',
                      background: 'rgba(255,255,255,0.97)',
                      borderRadius: '22px',
                      padding: '18px',
                      boxShadow: '0 20px 55px rgba(15, 23, 42, 0.18)'
                    }}
                  >
                    <div
                      style={{
                        fontSize: '0.78rem',
                        color: '#b7832f',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        marginBottom: '8px',
                        fontWeight: 700
                      }}
                    >
                      {hero?.floatingCard?.badge || 'Capacidade de execução'}
                    </div>

                    <div
                      style={{
                        fontWeight: 800,
                        fontSize: '1.12rem',
                        marginBottom: '6px',
                        color: '#0d2e63',
                        lineHeight: 1.3
                      }}
                    >
                      {hero?.floatingCard?.title || 'Atuação técnica com organização, experiência e segurança.'}
                    </div>

                    <div style={{ color: '#607089', fontSize: '0.95rem', lineHeight: 1.6 }}>
                      {hero?.floatingCard?.text ||
                        'Uma estrutura preparada para atender diferentes frentes operacionais com consistência e responsabilidade.'}
                    </div>
                  </motion.div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <section style={{ marginTop: '0', position: 'relative', zIndex: 3, paddingBottom: '20px' }}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
            style={{
              background: 'rgba(255,255,255,0.94)',
              borderRadius: '28px',
              boxShadow: '0 24px 70px rgba(15, 23, 42, 0.08)',
              border: '1px solid rgba(15, 23, 42, 0.05)',
              padding: '30px'
            }}
          >
            <motion.div
              className="row g-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
{differentials.map((item, index) => {
  const Icon = differentialIcons[item.title] || FiAward;

  return (
    <div className="col-lg-4" key={item.id || index}>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, delay: 0.15 + index * 0.08, ease: [0.22, 1, 0.36, 1] }}
        whileHover={{ y: -8, boxShadow: '0 24px 48px rgba(13, 46, 99, 0.08)' }}
        style={{
          background: '#ffffff',
          borderRadius: '22px',
          padding: '28px 24px',
          height: '100%',
          border: '1px solid rgba(13, 46, 99, 0.06)'
        }}
      >
        <div
          style={{
            width: '54px',
            height: '54px',
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #edf3ff, #f7efe1)',
            marginBottom: '18px',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#0d4fb3',
            fontSize: '1.4rem'
          }}
        >
          <Icon />
        </div>

        <h3 style={{ fontSize: '1.22rem', fontWeight: 800, marginBottom: '12px', color: '#0d2e63' }}>
          {item.title}
        </h3>

        <p style={{ color: '#607089', lineHeight: 1.8, marginBottom: 0 }}>
          {item.text}
        </p>
      </motion.div>
    </div>
  );
})}
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section style={{ padding: '110px 0 70px' }}>
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              <motion.div {...sectionReveal}>
                <SectionTag>{about.tag || 'Sobre a RealEnergy'}</SectionTag>
                <SectionTitle>
                  {about.title || 'Tradição, capacidade técnica e compromisso com a qualidade em cada entrega.'}
                </SectionTitle>
                <SectionText maxWidth="620px">
                  {about.text1 ||
                    'A RealEnergy é uma organização constituída em 1992, com atuação voltada à representação de materiais elétricos e à execução de serviços em energia, infraestrutura, operação e manutenção.'}
                </SectionText>
                <div style={{ height: '10px' }} />
                <SectionText maxWidth="620px">
                  {about.text2 ||
                    'Ao longo de sua trajetória, consolidou experiência em diferentes segmentos, mantendo foco em segurança, desempenho operacional e soluções alinhadas às necessidades de cada projeto.'}
                </SectionText>

                <motion.div
                  className="row g-3 mt-2"
                  variants={stagger}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, amount: 0.2 }}
                >
                  {aboutCards.map((item, index) => (
                    <div className="col-md-6" key={item.id || index}>
                      <motion.div
                        variants={fadeUp}
                        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                        whileHover={{ y: -6 }}
                        style={{
                          padding: '20px 22px',
                          borderRadius: '20px',
                          background: '#ffffff',
                          boxShadow: '0 16px 40px rgba(15, 23, 42, 0.06)',
                          border: '1px solid rgba(13, 46, 99, 0.05)',
                          height: '100%'
                        }}
                      >
                        <div style={{ fontWeight: 800, marginBottom: '8px', color: '#0d2e63' }}>{item.title}</div>
                        <div style={{ color: '#607089', lineHeight: 1.7 }}>{item.text}</div>
                      </motion.div>
                    </div>
                  ))}
                </motion.div>
              </motion.div>
            </div>

            <div className="col-lg-6">
              <motion.div {...sectionReveal} style={{ position: 'relative', minHeight: '560px' }}>
                <motion.img
                  whileHover={{ y: -6, scale: 1.01 }}
                  transition={{ duration: 0.35 }}
                  src={aboutImage1}
                  alt="Construção Civil"
                  style={{
                    width: '78%',
                    height: '420px',
                    objectFit: 'cover',
                    borderRadius: '30px',
                    boxShadow: '0 22px 60px rgba(15, 23, 42, 0.14)',
                    display: 'block'
                  }}
                />
                <motion.img
                  whileHover={{ y: -6, scale: 1.01 }}
                  transition={{ duration: 0.35 }}
                  src={aboutImage2}
                  alt="Iluminação"
                  style={{
                    width: '58%',
                    height: '260px',
                    objectFit: 'cover',
                    borderRadius: '26px',
                    boxShadow: '0 22px 60px rgba(15, 23, 42, 0.14)',
                    position: 'absolute',
                    right: 0,
                    bottom: 0,
                    border: '8px solid #f7f9fc'
                  }}
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <section style={{ padding: '20px 0 100px' }}>
        <div className="container">
          <motion.div {...sectionReveal} className="text-center" style={{ maxWidth: '820px', margin: '0 auto 54px' }}>
            <SectionTag>{homeData?.services?.tag || 'Áreas de atuação'}</SectionTag>
            <SectionTitle center>
              {homeData?.services?.title ||
                'Soluções estruturadas para diferentes demandas em energia, infraestrutura e serviços especializados.'}
            </SectionTitle>
            <SectionText center>
              {homeData?.services?.text ||
                'Com atuação diversificada e experiência acumulada ao longo de décadas, a RealEnergy oferece suporte técnico e operacional em frentes essenciais para o desenvolvimento de projetos e serviços públicos e privados.'}
            </SectionText>
          </motion.div>

          <motion.div
            className="row g-4"
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
          >
            {services.map((service, index) => (
              <div className="col-md-6 col-xl-4" key={service.id || index}>
                <motion.div
                  variants={fadeUp}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ y: -10 }}
                  style={{
                    background: '#ffffff',
                    borderRadius: '26px',
                    overflow: 'hidden',
                    height: '100%',
                    boxShadow: '0 18px 48px rgba(15, 23, 42, 0.07)',
                    border: '1px solid rgba(13, 46, 99, 0.05)',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <div style={{ position: 'relative', overflow: 'hidden' }}>
                    <motion.img
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.45, ease: 'easeOut' }}
                      src={service.imageUrl}
                      alt={service.title}
                      style={{ width: '100%', height: '240px', objectFit: 'cover', display: 'block' }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(to top, rgba(8,20,41,0.5), rgba(8,20,41,0.05))'
                      }}
                    />
                  </div>

                  <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#0d2e63', marginBottom: '12px' }}>
                      {service.title}
                    </h3>
                    <p style={{ color: '#607089', lineHeight: 1.8, flex: 1, marginBottom: '18px' }}>{service.description}</p>
                    <motion.div whileHover={{ x: 4 }} transition={{ duration: 0.2 }}>
                      <Link
                        to={service.link || '/about'}
                        style={{
                          textDecoration: 'none',
                          color: '#0d4fb3',
                          fontWeight: 700,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        Explorar serviço <span>→</span>
                      </Link>
                    </motion.div>
                  </div>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <section style={{ padding: '0 0 100px' }}>
        <div className="container">
          <motion.div
            {...sectionReveal}
            style={{
              background: 'linear-gradient(135deg, #ffffff, #f8fbff)',
              borderRadius: '30px',
              padding: '38px',
              boxShadow: '0 24px 60px rgba(15, 23, 42, 0.08)',
              border: '1px solid rgba(15, 23, 42, 0.05)'
            }}
          >
            <div className="row align-items-center g-4">
              <div className="col-lg-7">
                <SectionTag>{careers.tag || 'Inclusão e oportunidades'}</SectionTag>
                <SectionTitle maxWidth="640px">{careers.title || 'Trabalhe conosco'}</SectionTitle>
                <SectionText maxWidth="640px">
                  {careers.text ||
                    'A RealEnergy valoriza a diversidade e acredita na construção de um ambiente mais inclusivo. Se você é pessoa com deficiência, cadastre seu currículo e participe do nosso processo seletivo.'}
                </SectionText>
              </div>
              <div className="col-lg-5 text-lg-end">
                <motion.div whileHover={{ y: -2, scale: 1.01 }} whileTap={{ scale: 0.98 }}>
                  <Link to={careers?.button?.link || '/trabalheconosco'} style={primaryButton}>
                    {careers?.button?.text || 'Cadastrar currículo'}
                  </Link>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section style={{ padding: '0 0 100px' }}>
        <div className="container">
          <motion.div
            {...sectionReveal}
            style={{
              background: 'linear-gradient(180deg, #08172f 0%, #0b1f41 100%)',
              borderRadius: '34px',
              padding: '44px 26px 22px',
              boxShadow: '0 32px 80px rgba(2, 8, 23, 0.22)',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '-60px',
                right: '-60px',
                width: '220px',
                height: '220px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(214,160,74,0.22), transparent 68%)'
              }}
            />

            <div className="text-center" style={{ maxWidth: '760px', margin: '0 auto 30px', position: 'relative', zIndex: 2 }}>
              <SectionTag light>{portfolio.tag || 'Portfólio visual'}</SectionTag>
              <SectionTitle light center>
                {portfolio.title || 'Obras e operações que representam a experiência e a capacidade de entrega da RealEnergy.'}
              </SectionTitle>
              <SectionText light center>
                {portfolio.text ||
                  'Uma seleção visual de projetos e frentes de atuação que ajudam a apresentar a dimensão dos serviços executados pela empresa.'}
              </SectionText>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.12 }}
              style={{ position: 'relative', zIndex: 2 }}
            >
              <LightBox className="sliderCarrousel" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section style={{ padding: '0 0 110px' }}>
        <div className="container">
          <motion.div
            {...sectionReveal}
            style={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: '34px',
              padding: '64px 30px',
              backgroundImage: `linear-gradient(135deg, rgba(9,28,59,0.94), rgba(14,63,148,0.9)), url(${finalCtaBackground})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              textAlign: 'center',
              boxShadow: '0 28px 72px rgba(15, 23, 42, 0.16)'
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'radial-gradient(circle at top center, rgba(255,255,255,0.1), transparent 28%)'
              }}
            />

            <div style={{ maxWidth: '800px', margin: '0 auto', position: 'relative', zIndex: 2 }}>
              <SectionTag light>{finalCta.tag || 'Entre em contato'}</SectionTag>
              <SectionTitle light center maxWidth="800px">
                {finalCta.title || 'Uma empresa preparada para apoiar projetos com seriedade, estrutura e capacidade técnica.'}
              </SectionTitle>
              <SectionText light center maxWidth="760px">
                {finalCta.text ||
                  'Fale com a RealEnergy para apresentar sua necessidade, projeto ou demanda operacional. Nossa equipe está pronta para contribuir com soluções alinhadas à realidade do seu desafio.'}
              </SectionText>

              <div style={{ display: 'flex', justifyContent: 'center', gap: '14px', flexWrap: 'wrap', marginTop: '28px' }}>
                <motion.div whileHover={{ y: -2, scale: 1.01 }} whileTap={{ scale: 0.98 }}>
                  <Link to={finalCta?.primaryButton?.link || '/contato'} style={primaryButton}>
                    {finalCta?.primaryButton?.text || 'Entrar em contato'}
                  </Link>
                </motion.div>

                <motion.div whileHover={{ y: -2, scale: 1.01 }} whileTap={{ scale: 0.98 }}>
                  <Link to={finalCta?.secondaryButton?.link || '/about'} style={secondaryButton}>
                    {finalCta?.secondaryButton?.text || 'Ver mais sobre a empresa'}
                  </Link>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <SocialItens />
      <Footer />
    </div>
  );
}

export default Home;